import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  callAPI,
  getLocalValue,
  LoacalVariables,
  Method,
  updateMainHeader,
} from "../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import {
  ORDER_COLORS as COLORS,
  orderFieldSx as fieldSx,
  orderFilterChipSx as dayChipSx,
} from "../../Components/Orders/OrderCardParts";
import {
  DEFAULT_GENERAL_SETTINGS,
  GeneralSettings as Settings,
  loadGeneralSettings,
  saveGeneralSettings,
  WEEK_DAYS,
} from "./settingsStore";

const Section: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "16px", border: `1px solid ${COLORS.border}` }}>
    <Typography sx={{ fontWeight: 700, color: COLORS.dark, fontSize: "1rem" }}>{title}</Typography>
    {subtitle && <Typography sx={{ color: COLORS.muted, fontSize: "0.8rem", mt: 0.25 }}>{subtitle}</Typography>}
    <Divider sx={{ my: 2, borderColor: COLORS.border }} />
    {children}
  </Paper>
);

const SettingSwitch: React.FC<{ label: string; description: string; checked: boolean; onChange: (value: boolean) => void }> = ({
  label,
  description,
  checked,
  onChange,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 0.75 }}>
    <Box>
      <Typography sx={{ fontWeight: 600, color: COLORS.dark, fontSize: "0.9rem" }}>{label}</Typography>
      <Typography sx={{ color: COLORS.muted, fontSize: "0.8rem" }}>{description}</Typography>
    </Box>
    <FormControlLabel
      sx={{ m: 0 }}
      label=""
      control={
        <Switch
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ "aria-label": label }}
          sx={{ "& .Mui-checked": { color: COLORS.primary }, "& .Mui-checked + .MuiSwitch-track": { bgcolor: COLORS.primary } }}
        />
      }
    />
  </Box>
);

const ProfileRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem" }}>{label}</Typography>
    <Typography sx={{ color: COLORS.dark, fontSize: "0.9rem", fontWeight: 500, wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Grid>
);

export const GeneralSettings: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const vendorId: string = getLocalValue(LoacalVariables.UserId) || "";
  const isPharmacy = getLocalValue(LoacalVariables.UserType) === "pharmacy";

  const [settings, setSettings] = useState<Settings>(DEFAULT_GENERAL_SETTINGS);
  const [savedSettings, setSavedSettings] = useState<Settings>(DEFAULT_GENERAL_SETTINGS);
  const [profile, setProfile] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    updateMainHeader("General Settings");
    breadCrumbsStore.updateBreadCrumbs([{ Settings: "/settings/general" }, { "General Settings": "/settings/general" }]);
  }, []);

  useEffect(() => {
    if (!vendorId) return;
    loadGeneralSettings(vendorId).then((loaded) => {
      setSettings(loaded);
      setSavedSettings(loaded);
    });
    // Business details come from the existing vendor profile
    callAPI("VendorProfile/user", Method.GET, null, vendorId)
      .then((response: any) => setProfile(response?.data || null))
      .catch((e: any) => console.error("Failed to load profile:", e));
  }, [vendorId]);

  const update = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const toggleDay = (day: string) =>
    update(
      "workingDays",
      settings.workingDays.includes(day)
        ? settings.workingDays.filter((d) => d !== day)
        : WEEK_DAYS.filter((d) => d === day || settings.workingDays.includes(d))
    );

  const hoursError =
    settings.openingTime && settings.closingTime && settings.closingTime <= settings.openingTime
      ? "Closing time must be after opening time"
      : "";
  const daysError = settings.workingDays.length === 0 ? "Select at least one working day" : "";

  const isDirty = useMemo(() => JSON.stringify(settings) !== JSON.stringify(savedSettings), [settings, savedSettings]);

  const handleSave = async () => {
    if (hoursError || daysError || !vendorId) return;
    setSaving(true);
    try {
      await saveGeneralSettings(vendorId, settings);
      setSavedSettings(settings);
      showMessage("Settings saved", "success");
    } catch (e) {
      console.error("Failed to save settings:", e);
      showMessage("Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const address = profile
    ? [profile.addressLine1, profile.addressLine2, profile.city, profile.state, profile.pincode].filter(Boolean).join(", ")
    : "";

  return (
    <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2, maxWidth: 960 }}>
      {/* Business details (read-only, edited from the profile page) */}
      <Section title="Business details" subtitle="Shown to customers. Edit these from your profile.">
        <Grid container spacing={2}>
          <ProfileRow label={isPharmacy ? "Pharmacy name" : "Lab name"} value={profile?.companyName} />
          <ProfileRow label="Contact person" value={profile?.name} />
          <ProfileRow label="Phone" value={profile?.phoneNumber} />
          <ProfileRow label="Email" value={profile?.email} />
          <ProfileRow label="Licence number" value={profile?.licenceNumber} />
          <ProfileRow label="GST number" value={profile?.gstNumber} />
          <Grid item xs={12}>
            <Typography sx={{ color: COLORS.muted, fontSize: "0.75rem" }}>Address</Typography>
            <Typography sx={{ color: COLORS.dark, fontSize: "0.9rem", fontWeight: 500 }}>{address || "-"}</Typography>
          </Grid>
        </Grid>
        <Button
          variant="outlined"
          onClick={() => navigate(`/profile/edit/${profile?.userId || vendorId}`)}
          sx={{ mt: 2, borderColor: COLORS.primary, color: COLORS.primary, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
        >
          Edit profile
        </Button>
      </Section>

      {/* Availability */}
      <Section title="Availability" subtitle={`When your ${isPharmacy ? "pharmacy" : "lab"} takes new orders.`}>
        <SettingSwitch
          label="Accepting new orders"
          description="Turn off to pause new orders temporarily, e.g. during a holiday."
          checked={settings.acceptingOrders}
          onChange={(value) => update("acceptingOrders", value)}
        />
        <SettingSwitch
          label={isPharmacy ? "Home delivery" : "Home sample collection"}
          description={
            isPharmacy
              ? "Offer delivery to the customer's address."
              : "Offer sample collection at the patient's address."
          }
          checked={settings.homeService}
          onChange={(value) => update("homeService", value)}
        />

        <Typography sx={{ fontWeight: 600, color: COLORS.dark, fontSize: "0.9rem", mt: 2, mb: 1 }}>Working days</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {WEEK_DAYS.map((day) => (
            <Chip key={day} label={day} onClick={() => toggleDay(day)} sx={dayChipSx(settings.workingDays.includes(day))} />
          ))}
        </Box>
        {daysError && <Typography sx={{ color: "#D32F2F", fontSize: "0.75rem", mt: 0.5 }}>{daysError}</Typography>}

        <Typography sx={{ fontWeight: 600, color: COLORS.dark, fontSize: "0.9rem", mt: 2.5, mb: 1.5 }}>Working hours</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="time"
              label="Opens at"
              value={settings.openingTime}
              onChange={(e) => update("openingTime", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={fieldSx}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              fullWidth
              size="small"
              type="time"
              label="Closes at"
              value={settings.closingTime}
              onChange={(e) => update("closingTime", e.target.value)}
              InputLabelProps={{ shrink: true }}
              error={!!hoursError}
              helperText={hoursError}
              sx={fieldSx}
            />
          </Grid>
        </Grid>
      </Section>

      {/* Notifications */}
      <Section title="Notifications" subtitle="How we tell you about new orders.">
        <SettingSwitch
          label="Email notifications"
          description={`Send new order alerts to ${profile?.email || "your registered email"}.`}
          checked={settings.emailNotifications}
          onChange={(value) => update("emailNotifications", value)}
        />
        <SettingSwitch
          label="SMS notifications"
          description={`Send new order alerts to ${profile?.phoneNumber || "your registered phone number"}.`}
          checked={settings.smsNotifications}
          onChange={(value) => update("smsNotifications", value)}
        />
      </Section>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, pb: 2 }}>
        <Button
          disabled={!isDirty || saving}
          onClick={() => setSettings(savedSettings)}
          sx={{ color: COLORS.muted, textTransform: "none", fontWeight: 600 }}
        >
          Discard changes
        </Button>
        <Button
          variant="contained"
          disabled={!isDirty || saving || !!hoursError || !!daysError}
          onClick={handleSave}
          sx={{
            bgcolor: COLORS.primary,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": { bgcolor: COLORS.dark, boxShadow: "none" },
          }}
        >
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </Box>
    </Box>
  );
};

export default GeneralSettings;
